import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { DragDropContext } from 'react-beautiful-dnd';
import { withTranslation } from 'react-i18next';
import { AlertMessage, SimpleModal } from 'shared/components';
import {
  SeatingChartGrid,
  SeatingChartSelectionBar,
  StudentsList,
  SearchExamineeModal,
} from 'features/seating-chart';
import * as seatingChartActions from 'features/seating-chart/duck';
import * as roomActions from 'features/room-builder/duck';
import * as tcEventActions from 'features/tc-event-dashboard/duck';
import { getAllStudents } from 'features/seating-chart/selectors';
import {
  cloneDeep,
  concat,
  differenceBy,
  differenceWith,
  isEqual,
  find,
  filter,
  isEmpty,
} from 'lodash';
import { isRoomOnline } from 'shared/helpers';

import './SeatingChartUsedSeats.scss';

class SeatingChartUsedSeats extends Component {
  constructor(props) {
    super(props);

    this.state = {
      discardModalOpen: false,
      searchExamineeModalOpen: false,
      deselectRegModalOpen: false,
      isDirty: false,
      seatsData: [],
      selectedSeat: {},
      deassignStudent: null,
      studentsList: [],
      selectedStudent: null,
      showAlertMessage: false,
      unassignedStudentsWithMaterials: [],
    };

    this.toggleSelectSeat = this.toggleSelectSeat.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.closeDiscardModal = this.closeDiscardModal.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
    this.move = this.move.bind(this);
    this.handleSeatSelect = this.handleSeatSelect.bind(this);
    this.handleDeassignStudent = this.handleDeassignStudent.bind(this);
    this.showDeselectRegModal = this.showDeselectRegModal.bind(this);
    this.toggleDeselectRegModal = this.toggleDeselectRegModal.bind(this);
    this.toggleSearchExamineeModal = this.toggleSearchExamineeModal.bind(this);
    this.removeAlertMessage = this.removeAlertMessage.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
    this.seatingChart = React.createRef();

    const self = this;
    window.onbeforeunload = () => {
      if (self.state.isDirty) {
        return true;
      }
    };
  }

  componentDidMount() {
    const { actions, room, eventId } = this.props;

    actions.getStudentsList(room.id);
    actions.getRooms(+eventId);
    actions.getTestCenterCycle(eventId);

    this.setState({
      seatsData: this.props.seatsData,
    });
  }

  componentWillUnmount() {
    this.setState({
      seatsData: [],
    });
    Array.from(document.querySelectorAll('[data-bs-toggle="popover"]')).forEach((el) =>
      bootstrap.Popover.getOrCreateInstance(el).hide()
    );
  }

  componentDidUpdate() {
    Array.from(document.querySelectorAll('[data-bs-toggle="popover"]')).forEach((el) =>
      bootstrap.Popover.getOrCreateInstance(el).enable()
    );

    //requred for popovers on draggable components
    $('[data-bs-toggle="popover"], .close').on('click', (e) => {
      if (e.defaultPrevented) {
        return;
      }
      if (e.currentTarget.id.indexOf('deassign-') !== -1) {
        document.getElementById(e.currentTarget.id).focus();
      } else {
        if (e.currentTarget !== e.target && e.target.tabIndex >= 0) {
          e.target.focus();
        } else {
          e.currentTarget.focus();
        }
      }
    });
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    let data = prevState.seatsData.flat().filter((n) => n);
    return {
      studentsList: filter(
        nextProps.allStudents,
        (student) => !find(data, (s) => s.id === student.id)
      ),
    };
  }

  onDragEnd(result) {
    const { source, destination } = result;
    this.move(source, destination);
  }

  onDragStart() {
    Array.from(document.querySelectorAll('[data-bs-toggle="popover"]')).forEach((el) =>
      bootstrap.Popover.getOrCreateInstance(el).hide()
    );
  }

  async move(source, destination) {
    const { seatsData, unassignedStudentsWithMaterials } = this.state;
    const { room, roomTypeMaterialTypes, allStudents } = this.props;

    let seats = cloneDeep(seatsData);

    // dropped outside the list
    if (!destination) {
      return;
    }

    if (
      source.droppableId === 'droppable-students' &&
      destination.droppableId === 'droppable-students'
    ) {
      // do nothing
    }

    if (
      source.droppableId === 'droppable-students' &&
      destination.droppableId !== 'droppable-students'
    ) {
      // add student to SC
      const index = destination.droppableId.split('-');
      const row = +index[0];
      const col = +index[1];
      let student = find(unassignedStudentsWithMaterials, (student) => student.id === source.index);

      if (isEmpty(student)) {
        student = find(allStudents, (student) => student.id === source.index);
      }
      if (!seats[row]) {
        seats[row] = [];
      }

      if (seats[row][col] && seats[row][col].regMaterials && seats[row][col].regMaterials.length) {
        let materialsArr = [];
        seats[row][col].regMaterials.map((seatMaterial) => {
          let material = find(roomTypeMaterialTypes, {
            id: seatMaterial.materialTypeId,
          });
          materialsArr.push(`${material.name}: ${seatMaterial.serialNumber}`);
        });

        this.setState({
          selectedSeat: {
            ...seats[row][col],
            materials: materialsArr,
          },
        });
        let confirm = await this.showDeselectRegModal();
        if (!confirm) {
          return;
        }
        unassignedStudentsWithMaterials.push(seats[row][col]);
      }

      seats[row][col] = {
        ...student,
        seatRow: row,
        seatCol: col,
        roomId: room.id,
        unassignedStudentsWithMaterials,
      };
    }

    if (
      source.droppableId !== 'droppable-students' &&
      destination.droppableId === 'droppable-students'
    ) {
      // remove student from SC
      const index = source.droppableId.split('-');
      const row = +index[0];
      const col = +index[1];

      if (seats[row][col].regMaterials && seats[row][col].regMaterials.length) {
        let materialsArr = [];
        seats[row][col].regMaterials.map((seatMaterial) => {
          let material = find(roomTypeMaterialTypes, {
            id: seatMaterial.materialTypeId,
          });
          materialsArr.push(`${material.name}: ${seatMaterial.serialNumber}`);
        });
        this.setState({
          selectedSeat: {
            ...seats[row][col],
            materials: materialsArr,
          },
        });

        let confirm = await this.showDeselectRegModal();
        if (!confirm) {
          return;
        }
      }

      seats[row][col] = null;
    }

    if (
      source.droppableId !== 'droppable-students' &&
      destination.droppableId !== 'droppable-students'
    ) {
      // move student in SC
      const indexSource = source.droppableId.split('-');
      const rowSource = +indexSource[0];
      const colSource = +indexSource[1];

      const indexDestination = destination.droppableId.split('-');
      const rowDestination = +indexDestination[0];
      const colDestination = +indexDestination[1];

      if (!seats[rowDestination]) {
        seats[rowDestination] = [];
      }

      if (!seats[rowDestination][colDestination]) {
        seats[rowDestination][colDestination] = {};
      }

      const aux = cloneDeep(seats[rowDestination][colDestination]);
      seats[rowDestination][colDestination] = {
        ...seats[rowSource][colSource],
        seatRow: rowDestination,
        seatCol: colDestination,
      };

      if (!isEmpty(aux)) {
        seats[rowSource][colSource] = {
          ...aux,
          seatRow: rowSource,
          seatCol: colSource,
        };
      } else {
        seats[rowSource][colSource] = null;
      }
    }

    const data = seats.flat().filter((n) => n);
    this.setState({
      studentsList: filter(allStudents, (student) => !find(data, (s) => s.id === student.id)),
      seatsData: seats,
      selectedStudent: null,
      isDirty: true,
    });
  }

  handleSeatSelect(studentId) {
    if (!isEmpty(this.state.selectedStudent) && this.state.selectedStudent.index === studentId) {
      this.setState({
        selectedStudent: null,
      });
    } else {
      this.setState({
        selectedStudent: {
          droppableId: 'droppable-students',
          index: studentId,
        },
        isDirty: true,
      });
    }
  }

  toggleSelectSeat(i, j) {
    const { selectedStudent } = this.state;

    if (!isEmpty(selectedStudent)) {
      this.move(selectedStudent, { droppableId: `${i}-${j}` });
    }
  }

  handleDeassignStudent(index) {
    this.move({ droppableId: index }, { droppableId: 'droppable-students' });
  }

  toggleDeselectRegModal(confirm) {
    this.setState({
      deselectRegModalOpen: false,
      selectedSeat: {},
    });
    this.resolve(confirm);
  }

  showDeselectRegModal() {
    this.setState({ deselectRegModalOpen: true });
    return new Promise((res) => {
      this.resolve = res;
    });
  }

  async handleSave() {
    const { actions, room } = this.props;

    const { seatsData } = this.state;

    const data = seatsData.flat().filter((n) => n);
    const oldSeatsData = this.props.seatsData.flat().filter((n) => n);
    const regs = data.map((item) => ({
      id: item.id,
      seatCol: item.seatCol,
      seatRow: item.seatRow,
    }));
    const oldRegs = oldSeatsData.map((item) => ({
      id: item.id,
      seatCol: item.seatCol,
      seatRow: item.seatRow,
    }));

    // get all unassigned regs
    let unassignedRegs = differenceBy(oldRegs, regs, 'id').map((item) => {
      return {
        ...item,
        seatCol: null,
        seatRow: null,
      };
    });
    // differenceWith: to get all changed regs
    const regsList = concat(unassignedRegs, differenceWith(regs, oldRegs, isEqual));

    if (regsList.length) {
      await actions.assignRegs(room.locationEvent.id, {
        roomId: room.id,
        regs: regsList,
      });
      await actions.getRegSummaries(room.id);
      sessionStorage.setItem('successSavedMessage', 'seatingChart.successMessage');
    }

    this.props.history.goBack();
  }

  toggleSearchExamineeModal(e, showAlertMessage) {
    this.setState((prevState) => ({
      searchExamineeModalOpen: !prevState.searchExamineeModalOpen,
      showAlertMessage: !!showAlertMessage,
    }));
  }

  handleBack() {
    if (this.state.isDirty) {
      this.setState({
        discardModalOpen: true,
      });
    } else {
      this.props.history.goBack();
    }
  }

  closeDiscardModal(goBack) {
    if (goBack) {
      this.props.history.goBack();
    }
    this.setState({
      discardModalOpen: false,
    });
  }

  removeAlertMessage() {
    this.setState({
      showAlertMessage: false,
    });
  }

  render() {
    const { room, roomTypeMaterialTypes, allStudents } = this.props;

    const {
      discardModalOpen,
      deselectRegModalOpen,
      seatsData,
      selectedSeat,
      studentsList,
      selectedStudent,
      searchExamineeModalOpen,
      showAlertMessage,
    } = this.state;

    const totalSeats = allStudents.length;
    let countSeats = 0;
    seatsData &&
      seatsData.map((seatsRow) => seatsRow && seatsRow.map((seat) => seat && countSeats++));

    const hasNowManagedFlag =
      this.props.testCenterCycle.testCenterProgram?.testCenter.nowManagedFlag;

    return (
      <div className="seating-chart-used-seats" id="seating-chart-used-seats">
        <div className="container title-container mb-30">
          <h4 className="pt-3">{this.props.t('seatingChart.titleExamenieeSeats')}</h4>
          <p className="text-strong">
            {this.props.t(
              isRoomOnline(room.roomType)
                ? 'seatingChart.subtitleExamenieeSeatsOnline'
                : 'seatingChart.subtitleExamenieeSeats'
            )}
          </p>
          <p>
            {this.props.t('seatingChart.searchExaminee')}{' '}
            <span
              tabIndex="0"
              onClick={this.toggleSearchExamineeModal}
              className="search-examinee-link">
              {this.props.t('common.labels.click')}
            </span>
          </p>
        </div>

        <div className="bg-white-smoke">
          <div className="container display-2-columns">
            <DragDropContext onDragEnd={this.onDragEnd} onDragStart={this.onDragStart}>
              <StudentsList
                studentsList={studentsList}
                selectedStudent={selectedStudent}
                handleSeatSelect={this.handleSeatSelect}
                hasNowManagedFlag={hasNowManagedFlag}
              />
              <SeatingChartGrid
                rows={room.seatingRowCount}
                cols={room.seatingColumnCount}
                seatsData={seatsData}
                type="used-seats"
                handleSeatClick={this.toggleSelectSeat}
                roomTypeMaterialTypes={roomTypeMaterialTypes}
                maxColsDisplayed={7}
                handleDeassignStudent={this.handleDeassignStudent}
                smallRefFlag
                hasNowManagedFlag={hasNowManagedFlag}
              />
            </DragDropContext>
          </div>
        </div>

        <SeatingChartSelectionBar
          count={countSeats}
          total={totalSeats}
          handleSave={this.handleSave}
          handleBack={this.handleBack}
          saveBtnLabel="seatingChart.saveSelectionLabel"
          selectedSeatsLabel="seatingChart.countSeatsSelected"
        />

        <SimpleModal
          isOpen={discardModalOpen}
          onCancel={() => this.closeDiscardModal(false)}
          onConfirm={() => this.closeDiscardModal(true)}
          confirmLabel={this.props.t('common.labels.yes')}
          cancelLabel={this.props.t('common.labels.no')}
          headline={this.props.t('common.backModal.title')}
          message={this.props.t('common.backModal.discardPageMessage')}
          buttonsReverseOrder
        />

        <SimpleModal
          isOpen={deselectRegModalOpen}
          onCancel={() => this.toggleDeselectRegModal(false)}
          onConfirm={() => this.toggleDeselectRegModal(true)}
          confirmLabel={this.props.t('common.labels.continue')}
          cancelLabel={this.props.t('common.labels.back')}
          headline={this.props.t('seatingChart.deselectSeatModalTitle')}
          message={this.props.t('seatingChart.deselectSeatModalMessage')}>
          {selectedSeat.materials &&
            selectedSeat.materials.map((material, i) => <div key={i}>{material}</div>)}
        </SimpleModal>

        {searchExamineeModalOpen && (
          <SearchExamineeModal
            toggleSearchExamineeModal={this.toggleSearchExamineeModal}
            eventId={+this.props.eventId}
            roomId={room.id}
            locationEventId={room.locationEvent.id}
          />
        )}

        <AlertMessage
          show={showAlertMessage}
          removeMessage={this.removeAlertMessage}
          message={this.props.t('seatingChart.searchExamineeModal.successMessage')}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    room: state.roomOptions.room || {},
    allStudents: getAllStudents(state),
    testCenterCycle: state.tcEvent.testCenterCycle || {},
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...roomActions,
        ...seatingChartActions,
        ...tcEventActions,
      },
      dispatch
    ),
  };
}

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(SeatingChartUsedSeats)
);

SeatingChartUsedSeats.propTypes = {
  t: PropTypes.func.isRequired,
};
SeatingChartUsedSeats.defaultProps = {
  allStudents: [],
};
